/**
 * HVP Client-Side Cascade Engine
 *
 * Performs cascade filtering entirely on the client using preloaded variation data.
 * This is ~100x faster than server-side AJAX (50ms vs 5000ms) because it avoids
 * WordPress startup overhead on every dropdown interaction.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { applyMixin } from './mixin.js';
import { ClientCascadeMatchMethods } from './client-cascade-match.js';

export class HVPClientCascade {
    /**
     * Constructor
     *
     * @param {Array} allVariations - All variation data from server
     * @param {Array} attributeOrder - Attribute names in cascade order
     * @param {boolean} debug - Enable debug logging
     */
    constructor(allVariations, attributeOrder, debug = false) {
        this.allVariations = allVariations || [];
        this.attributeOrder = attributeOrder || [];
        this.debug = debug;

        // Build lookup indexes for faster filtering
        this.variationsByAttribute = new Map();
        this.buildIndexes();

        this.log(`Initialized with ${this.allVariations.length} variations`);
    }

    /**
     * Build indexes for faster lookups
     * @private
     */
    buildIndexes() {
        // Index variations by each attribute value for faster filtering
        for (const attr of this.attributeOrder) {
            const normalized = this.normalizeAttributeName(attr);
            this.variationsByAttribute.set(normalized, new Map());
        }

        for (const variation of this.allVariations) {
            if (!variation.attributes) continue;

            for (const [attrKey, attrValue] of Object.entries(variation.attributes)) {
                const normalized = this.normalizeAttributeName(attrKey);
                const valueMap = this.variationsByAttribute.get(normalized);

                if (valueMap) {
                    if (!valueMap.has(attrValue)) {
                        valueMap.set(attrValue, []);
                    }
                    valueMap.get(attrValue).push(variation);
                }
            }
        }
    }

    /**
     * Check if client-side cascade is available
     *
     * @returns {boolean} True if we have variation data
     */
    isAvailable() {
        return this.allVariations.length > 0;
    }

    /**
     * Get available options for an attribute based on current selections
     *
     * This filters variations to find all valid options for the target attribute
     * given the current selections. Only considers selections ABOVE the target
     * in the attribute order (true cascade behavior).
     *
     * @param {Object} selections - Current attribute selections
     * @param {string} targetAttribute - Attribute to get options for
     * @returns {Array} Available options with label and value
     */
    getOptions(selections, targetAttribute) {
        const start = performance.now();
        const normalized = this.normalizeAttributeName(targetAttribute);
        const targetIndex = this.getAttributeIndex(normalized);

        this.log(`Getting options for ${normalized} (index ${targetIndex})`);
        this.log(`Current selections:`, selections);

        // Get selections ABOVE this attribute only (true cascade)
        const selectionsAbove = {};
        for (let i = 0; i < targetIndex; i++) {
            const attr = this.attributeOrder[i];
            const attrNormalized = this.normalizeAttributeName(attr);
            if (selections[attrNormalized]) {
                selectionsAbove[attrNormalized] = selections[attrNormalized];
            }
        }

        this.log(`Selections above:`, selectionsAbove);

        // Filter variations that match selections above
        const matchingVariations = this.filterVariations(selectionsAbove);

        this.log(`Matching variations: ${matchingVariations.length}`);

        // Extract unique values for target attribute
        const optionValues = new Set();
        const optionsMap = new Map(); // value -> label mapping

        for (const variation of matchingVariations) {
            if (!variation.attributes) continue;

            // Find the attribute value (check multiple key formats)
            let value = null;
            for (const [key, val] of Object.entries(variation.attributes)) {
                if (this.normalizeAttributeName(key) === normalized) {
                    value = val;
                    break;
                }
            }

            // '' = wildcard child ("Any …"): it offers every value this attribute
            // has anywhere on the product (Codex F02) instead of none.
            const candidates = value === '' ? this.knownValuesFor(normalized) : (value ? [value] : []);
            for (const candidate of candidates) {
                if (!optionValues.has(candidate)) {
                    optionValues.add(candidate);
                    // Generate label from slug (convert dashes to spaces, capitalize)
                    optionsMap.set(candidate, this.slugToLabel(candidate));
                }
            }
        }

        // Convert to array format expected by dropdown update
        const options = Array.from(optionsMap.entries()).map(([value, label]) => ({
            value: value,
            slug: value,
            label: label
        }));

        // Sort options alphabetically by label
        options.sort((a, b) => a.label.localeCompare(b.label));

        const elapsed = Math.round(performance.now() - start);
        this.log(`Found ${options.length} options for ${normalized} in ${elapsed}ms`);

        return options;
    }

    /**
     * Get batch options for all remaining attributes
     *
     * @param {Object} selections - Current attribute selections
     * @returns {Object} Options keyed by attribute name
     */
    getBatchOptions(selections) {
        const start = performance.now();
        const batchOptions = {};

        // Find the first unselected attribute index
        let firstUnselectedIndex = 0;
        for (let i = 0; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);
            if (!selections[normalized]) {
                firstUnselectedIndex = i;
                break;
            }
            firstUnselectedIndex = i + 1;
        }

        // Get options for each attribute from firstUnselectedIndex onwards
        for (let i = firstUnselectedIndex; i < this.attributeOrder.length; i++) {
            const attr = this.attributeOrder[i];
            const normalized = this.normalizeAttributeName(attr);
            batchOptions[normalized] = this.getOptions(selections, normalized);
        }

        const elapsed = Math.round(performance.now() - start);
        this.log(`Batch options for ${Object.keys(batchOptions).length} attributes in ${elapsed}ms`);

        return batchOptions;
    }

    /**
     * Get attribute index in order
     *
     * @param {string} attribute - Attribute name
     * @returns {number} Index or -1 if not found
     */
    getAttributeIndex(attribute) {
        const normalized = this.normalizeAttributeName(attribute);

        for (let i = 0; i < this.attributeOrder.length; i++) {
            if (this.normalizeAttributeName(this.attributeOrder[i]) === normalized) {
                return i;
            }
        }

        return -1;
    }

    /**
     * Normalize attribute name
     *
     * @param {string} name - Attribute name
     * @returns {string} Normalized name with pa_ prefix
     */

    /**
     * Every non-empty value an attribute takes across all variations (memoized).
     * Used to expand a wildcard ('' / "Any") child into concrete options.
     *
     * @param {string} normalized - Normalized attribute name
     * @returns {string[]}
     */
    knownValuesFor(normalized) {
        this._knownValues = this._knownValues || new Map();
        if (this._knownValues.has(normalized)) return this._knownValues.get(normalized);
        const values = new Set();
        for (const variation of this.allVariations) {
            if (!variation.attributes) continue;
            for (const [key, val] of Object.entries(variation.attributes)) {
                if (val && this.normalizeAttributeName(key) === normalized) values.add(val);
            }
        }
        const list = Array.from(values);
        this._knownValues.set(normalized, list);
        return list;
    }
    normalizeAttributeName(name) {
        if (!name) return '';

        // Remove 'attribute_' prefix if present
        let normalized = name.replace('attribute_', '');

        // Ensure 'pa_' prefix for product attributes
        if (!normalized.startsWith('pa_')) {
            normalized = 'pa_' + normalized.replace('pa_', '');
        }

        return normalized;
    }

    /**
     * Convert slug to human-readable label
     *
     * @param {string} slug - Slug like "4-0-full-color-single-sided"
     * @returns {string} Label like "4 0 Full Color Single Sided"
     */
    slugToLabel(slug) {
        if (!slug) return '';

        return slug
            .replace(/-/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    /**
     * Log message (debug mode only)
     *
     * @private
     */
    log(...args) {
        if (this.debug) {
            window.hvpData && window.hvpData.debug && console.log(
                '%c[HVP Client Cascade]',
                'background: #10b981; color: #fff; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
                ...args
            );
        }
    }

    /**
     * Get statistics about the cascade engine
     *
     * @returns {Object} Statistics
     */
    getStats() {
        return {
            variationCount: this.allVariations.length,
            attributeCount: this.attributeOrder.length,
            attributes: this.attributeOrder,
            indexedAttributes: Array.from(this.variationsByAttribute.keys())
        };
    }
}

// Variation matching lives in a sibling module (350-line rule).
applyMixin(HVPClientCascade.prototype, ClientCascadeMatchMethods);

export default HVPClientCascade;
